/**
 * Mutation hook to persist per-interface preference config.
 */

import { useMutation } from '@tanstack/react-query';
import { usePreferencesStore } from '@/stores/preferencesStore';
import {
  updateInterfaceConfig as updateInterfaceConfigAPI,
  getUserPreferences,
} from '@/services/preferences.service';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Read/write one interface config from the preferences store. */
export const usePreferencesConfig = (interfaceName: string) => {
  const { preferences, isLoaded, getInterfaceConfig, updateInterfaceConfig, setPreferences } =
    usePreferencesStore();

  const config = getInterfaceConfig(interfaceName);

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: (newConfig: Record<string, unknown>) =>
      updateInterfaceConfigAPI(interfaceName, newConfig),
    onMutate: (newConfig) => {
      updateInterfaceConfig(interfaceName, newConfig);
    },
    onError: async (error) => {
      // Roll back the optimistic store write from a fresh GET.
      try {
        const freshPrefs = await getUserPreferences();
        setPreferences(freshPrefs);
      } catch (refetchError) {
        console.error('Failed to refetch preferences:', refetchError);
      }
      toast.error(getErrorMessage(error));
    },
  });

  return {
    config,
    isLoaded,
    saveConfig,
    isSaving,
    preferences,
  };
};
